import { Capacitor } from '@capacitor/core';

export type SupportedPlatform = 'web' | 'android' | 'ios';

type StorageLike = Pick<
  Storage,
  'clear' | 'getItem' | 'key' | 'removeItem' | 'setItem'
> & {
  readonly length: number;
};

const createMemoryStorage = (): StorageLike => {
  const store = new Map<string, string>();

  return {
    clear() {
      store.clear();
    },
    getItem(key: string) {
      return store.get(key) ?? null;
    },
    key(index: number) {
      return Array.from(store.keys())[index] ?? null;
    },
    get length() {
      return store.size;
    },
    removeItem(key: string) {
      store.delete(key);
    },
    setItem(key: string, value: string) {
      store.set(key, value);
    },
  };
};

const detectPlatform = (): SupportedPlatform => {
  const detected = Capacitor.getPlatform();

  if (detected === 'android' || detected === 'ios') {
    return detected;
  }

  return 'web';
};

const safeWindow = typeof window !== 'undefined' ? window : undefined;
const platform = detectPlatform();
const platformSuffix = platform.toUpperCase();

const getEnv = (name: string): string | undefined => {
  const value = process.env[name];
  return value && value.trim().length > 0 ? value.trim() : undefined;
};

const getPlatformEnv = (name: string): string | undefined =>
  getEnv(`${name}_${platformSuffix}`) ?? getEnv(name);

const getPlatformEnvOrDefault = (name: string, fallback: string): string =>
  getPlatformEnv(name) ?? fallback;

const baseOrigin =
  safeWindow?.location.origin ?? (platform === 'web' ? 'http://localhost' : 'https://localhost');

const buildAppUrl = (path: string): string => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return new URL(normalizedPath, `${baseOrigin}/`).toString();
};

const resolveConfiguredUrl = (envName: string, fallbackPath: string): string =>
  getPlatformEnv(envName) ?? buildAppUrl(fallbackPath);

const legalPageSlugs = Object.freeze({
  contacts: 'contactos',
  legalNotice: 'aviso-legal',
  termsAndConditions: 'termos-e-condicoes',
  purchaseConditions: 'condicoes-de-compra',
  cancellationAndRefunds: 'cancelamento-reembolsos',
  privacyPolicy: 'privacidade',
  cookiesPolicy: 'cookies',
  disputeResolution: 'resolucao-litigios',
  complaintsBook: 'livro-reclamacoes',
});

type LegalPageSlug = typeof legalPageSlugs[keyof typeof legalPageSlugs];

const defaultLegalOrigin =
  getPlatformEnv('REACT_APP_LEGAL_BASE_URL') ??
  (platform === 'web' ? baseOrigin : 'https://astro.senti.pt');
const defaultOidcAuthority = getPlatformEnv('REACT_APP_OIDC_AUTHORITY') ?? 'https://auth.astro.senti.pt';
const defaultCheckoutReturnOrigin = new URL(
  getPlatformEnv('REACT_APP_CHECKOUT_RETURN_ORIGIN') ??
    (platform === 'web' ? baseOrigin : 'https://astro.senti.pt'),
).origin;

const buildLegalUrl = (slug: LegalPageSlug): string =>
  new URL(`/legal/${slug}`, `${defaultLegalOrigin}/`).toString();

const resolveLegalCanonicalUrl = (envName: string, slug: LegalPageSlug): string =>
  getPlatformEnv(envName) ?? buildLegalUrl(slug);

const publicLegalConfigurationUrl =
  getPlatformEnv('REACT_APP_LEGAL_PUBLIC_CONFIG_URL') ??
  new URL('/api/public/legal-configuration', `${defaultOidcAuthority}/`).toString();
const publicLegalPageContentBaseUrl = (
  getPlatformEnv('REACT_APP_LEGAL_PUBLIC_PAGE_CONTENT_BASE_URL') ??
  new URL('/api/public/legal-pages/', `${defaultOidcAuthority}/`).toString()
).replace(/\/?$/, '/');

// GR-001: product analytics. Both identifiers are public and ship inside the
// bundle; they are not secrets. Analytics stays off until both are configured.
const analyticsScriptUrl = getPlatformEnv('REACT_APP_ANALYTICS_SCRIPT_URL');
const analyticsWebsiteId = getPlatformEnv('REACT_APP_ANALYTICS_WEBSITE_ID');
const analyticsHostUrl = getPlatformEnv('REACT_APP_ANALYTICS_HOST_URL');

const localMemoryStorage = createMemoryStorage();
const sessionMemoryStorage = createMemoryStorage();

const resolveStorage = (storageType: 'local' | 'session'): StorageLike => {
  if (!safeWindow) {
    return storageType === 'local' ? localMemoryStorage : sessionMemoryStorage;
  }

  return storageType === 'local' ? safeWindow.localStorage : safeWindow.sessionStorage;
};

export const runtimeConfig = Object.freeze({
  platform,
  isNativePlatform: platform !== 'web',
  isWebPlatform: platform === 'web',
  appOrigin: baseOrigin,
  appUrl: buildAppUrl,
  apiBaseUrl: getPlatformEnv('REACT_APP_API_BASE_URL') ?? '',
  checkout: Object.freeze({
    returnOrigin: defaultCheckoutReturnOrigin,
  }),
  support: Object.freeze({
    email: getPlatformEnvOrDefault('REACT_APP_SUPPORT_EMAIL', 'support@senti.pt'),
    phone: getPlatformEnvOrDefault('REACT_APP_SUPPORT_PHONE', '966101852'),
  }),
  analytics: Object.freeze({
    enabled: Boolean(analyticsScriptUrl && analyticsWebsiteId),
    scriptUrl: analyticsScriptUrl ?? '',
    websiteId: analyticsWebsiteId ?? '',
    hostUrl: analyticsHostUrl ?? '',
  }),
  legal: Object.freeze({
    publicConfigUrl: publicLegalConfigurationUrl,
    publicPageContentBaseUrl: publicLegalPageContentBaseUrl,
    slugs: legalPageSlugs,
    canonicalUrls: Object.freeze({
      contacts: resolveLegalCanonicalUrl(
        'REACT_APP_LEGAL_CONTACTS_URL',
        legalPageSlugs.contacts
      ),
      legalNotice: resolveLegalCanonicalUrl(
        'REACT_APP_LEGAL_NOTICE_URL',
        legalPageSlugs.legalNotice
      ),
      termsAndConditions: resolveLegalCanonicalUrl(
        'REACT_APP_LEGAL_TERMS_AND_CONDITIONS_URL',
        legalPageSlugs.termsAndConditions
      ),
      purchaseConditions: resolveLegalCanonicalUrl(
        'REACT_APP_LEGAL_PURCHASE_CONDITIONS_URL',
        legalPageSlugs.purchaseConditions
      ),
      cancellationAndRefunds: resolveLegalCanonicalUrl(
        'REACT_APP_LEGAL_CANCELLATION_AND_REFUNDS_URL',
        legalPageSlugs.cancellationAndRefunds
      ),
      privacyPolicy: resolveLegalCanonicalUrl(
        'REACT_APP_LEGAL_PRIVACY_POLICY_URL',
        legalPageSlugs.privacyPolicy
      ),
      cookiesPolicy: resolveLegalCanonicalUrl(
        'REACT_APP_LEGAL_COOKIES_POLICY_URL',
        legalPageSlugs.cookiesPolicy
      ),
      disputeResolution: resolveLegalCanonicalUrl(
        'REACT_APP_LEGAL_DISPUTE_RESOLUTION_URL',
        legalPageSlugs.disputeResolution
      ),
      complaintsBook: resolveLegalCanonicalUrl(
        'REACT_APP_LEGAL_COMPLAINTS_BOOK_PAGE_URL',
        legalPageSlugs.complaintsBook
      ),
    }),
    externalUrls: Object.freeze({
      disputeResolutionEntity: getPlatformEnvOrDefault(
        'REACT_APP_LEGAL_RAL_ENTITY_URL',
        'https://www.cniacc.pt/'
      ),
      complaintsBook: getPlatformEnvOrDefault(
        'REACT_APP_LEGAL_COMPLAINTS_BOOK_URL',
        'https://www.livroreclamacoes.pt/Pedido/Reclamacao'
      ),
    }),
  }),
  nativeDeepLinkScheme:
    getPlatformEnv('REACT_APP_NATIVE_URL_SCHEME') ?? 'pt.senti.astroapp',
  oidc: Object.freeze({
    authority: defaultOidcAuthority,
    clientId: getPlatformEnv('REACT_APP_OIDC_CLIENT_ID') ?? 'frontend-client',
    scope:
      getPlatformEnv('REACT_APP_OIDC_SCOPE') ??
      'openid profile email roles api offline_access',
    redirectUri: resolveConfiguredUrl('REACT_APP_OIDC_REDIRECT_URI', '/signin-oidc'),
    postLogoutRedirectUri: resolveConfiguredUrl(
      'REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI',
      '/signout-callback-oidc'
    ),
    silentRedirectUri: resolveConfiguredUrl(
      'REACT_APP_OIDC_SILENT_REDIRECT_URI',
      '/silent-renew.html'
    ),
  }),
});

export const appStorage = Object.freeze({
  local: resolveStorage('local'),
  session: resolveStorage('session'),
});

const buildAppUrlWithState = (
  pathname: string,
  search: string,
  hash: string
): string => {
  const nextUrl = new URL(buildAppUrl(pathname));
  nextUrl.search = search;
  nextUrl.hash = hash;
  return nextUrl.toString();
};

export const isOidcCallbackRequest = (
  search: string = safeWindow?.location.search ?? ''
): boolean => search.includes('code=') && search.includes('state=');

export const mapNativeUrlToAppUrl = (url: string): string | null => {
  let parsed: URL;

  try {
    parsed = new URL(url);
  } catch {
    return null;
  }

  const customSchemeProtocol = `${runtimeConfig.nativeDeepLinkScheme}:`;
  const isCustomScheme = parsed.protocol === customSchemeProtocol;
  const isTrustedReturn =
    (parsed.protocol === 'https:' || parsed.protocol === 'http:') &&
    parsed.origin === runtimeConfig.checkout.returnOrigin;

  if (!isCustomScheme && !isTrustedReturn) {
    return null;
  }

  const route = isCustomScheme && parsed.hostname
    ? `/${parsed.hostname}${parsed.pathname}`
    : parsed.pathname || '/';

  if (route === '/signin-oidc' || route === '/signout-callback-oidc') {
    return buildAppUrlWithState(route, parsed.search, parsed.hash);
  }

  if (route.startsWith('/legal/')) {
    return buildAppUrlWithState(route, parsed.search, parsed.hash);
  }

  if (
    route === '/checkout/stripe/success' ||
    route === '/checkout/stripe/cancel'
  ) {
    return buildAppUrlWithState(route, parsed.search, parsed.hash);
  }

  if (route === '/dashboard' || route === '/') {
    return buildAppUrl('/dashboard');
  }

  return isCustomScheme ? buildAppUrl('/dashboard') : null;
};

export const replaceBrowserLocation = (url: string): void => {
  safeWindow?.location.replace(url);
};

export const navigateToAppPath = (path: string, replace = false): void => {
  if (!safeWindow) {
    return;
  }

  const nextUrl = buildAppUrl(path);

  if (replace) {
    safeWindow.location.replace(nextUrl);
    return;
  }

  safeWindow.location.assign(nextUrl);
};

export const reloadApp = (): void => {
  safeWindow?.location.reload();
};

export const clearAppStorage = (): void => {
  appStorage.local.clear();
  appStorage.session.clear();
};
